import { Anchor, Button, Flex, Image, Input, Text } from "@mantine/core";
import dayjs from "dayjs";
import { type SubmitHandler, useForm } from "react-hook-form";
import { useIsOnline, usePostApi } from "src/hooks";
import {
  AccountTitleWrapper,
  ButtonWrapper,
  FieldsSection,
  FirstUserSettingsContainer,
  UserName,
} from "src/pages/Settings/AccountDetails/AccountDetails.styled";
import { type IAccountDetailsForm } from "src/pages/Settings/AccountDetails/AccountDetails.types";
import { Notification } from "src/utils/Notification";
import { IconCheck } from "@tabler/icons-react";

interface Props {
  titleStrings: string[];
  rolename: string;
  formData: IAccountDetailsForm;
  refetch: () => void;
}

const AccountDetailsForm = ({
  titleStrings,
  rolename,
  formData,
  refetch,
}: Props) => {
  const { post, isLoading } = usePostApi();
  const isOnline = useIsOnline();

  const { dssActive, dssJoinedDate, ...defaultFormData } = formData;

  const { register, handleSubmit, reset } = useForm<IAccountDetailsForm>({
    defaultValues: defaultFormData,
  });

  const onSubmit: SubmitHandler<IAccountDetailsForm> = async data => {
    await post({
      request: "user-save-account",
      body: { params: data },
      onSuccess: () => {
        Notification(
          "Success",
          "Account details have been successfully changed.",
          "green",
        );
        reset(data);
        refetch?.();
      },
    });
  };

  return (
    <FirstUserSettingsContainer onSubmit={handleSubmit(onSubmit)}>
      <UserName>
        <AccountTitleWrapper>
          {titleStrings.map(e => (
            <Text key={e}>{e}</Text>
          ))}
        </AccountTitleWrapper>
        <Text mb={20}>{`Rank - ${rolename}`}</Text>
      </UserName>
      <FieldsSection>
        <Input.Wrapper label="First Name">
          <Input {...register("fname")} readOnly={!isOnline} />
        </Input.Wrapper>
        <Input.Wrapper mt={10} label="Surname">
          <Input {...register("sname")} readOnly={!isOnline} />
        </Input.Wrapper>
        <Input.Wrapper mt={10} label="Email">
          <Input {...register("email")} readOnly={!isOnline} />
        </Input.Wrapper>
        <Input.Wrapper mt={10} label="Personal Email">
          <Input {...register("personalEmail")} readOnly={!isOnline} />
        </Input.Wrapper>
        <Input.Wrapper mt={10} label="Phone Number">
          <Input
            // component={IMaskInput}
            // mask="+44 7000-000-000"
            // id={"1"}
            {...register("phoneno")}
            readOnly={!isOnline}
          />
        </Input.Wrapper>
        <Input.Wrapper mt={10} label="Mobile">
          <Input
            // component={IMaskInput}
            // mask="+44 20-0000-0000"
            // id={"2"}
            {...register("mobile")}
            readOnly={!isOnline}
          />
        </Input.Wrapper>
        <Flex maw={380} mt={50} mb={30}>
          <Anchor
            href="https://digitalseaservice.com/"
            target="_blank"
            rel="noopener noreferrer"
            size="lg">
            <Image src="assets/DSS.png" maw={270} mb={4} />
            Streamline your sea time.
            <Text size="sm" c="dimmed" mt={4}>
              Log, track, and verify your sea service effortlessly — all in one
              place, for MCA, PYA, USCG, Nautilus, and more. <br />
              <br />
              LJ eLogbooks Integration coming soon...
            </Text>
          </Anchor>
        </Flex>
        <Input.Wrapper mt={10} label="DSS API token">
          <Input {...register("dssToken")} readOnly={!isOnline} />
        </Input.Wrapper>
        <Text
          color={dssActive ? "text" : "grayText"}
          size="sm"
          mt={5}
          style={{ display: "flex", alignItems: "center", gap: 5 }}>
          {dssActive
            ? `DSS Joined on ${dayjs(dssJoinedDate).format("DD MMM YYYY")}`
            : "Not connected"}
          {dssActive && <IconCheck size={16} color="green" />}
        </Text>
      </FieldsSection>
      {isOnline && (
        <ButtonWrapper>
          <Button
            type="submit"
            variant="outline"
            size="xs"
            mt={20}
            loading={isLoading}>
            Save
          </Button>
        </ButtonWrapper>
      )}
    </FirstUserSettingsContainer>
  );
};

export default AccountDetailsForm;
